import React from 'react';
import { Image, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import * as Yup from "yup";
import InputField from "src/components/common/Inputs/InputField";
import { CustomButton } from "src/components/common/Buttons/CustomButton";
import { AuthHeader } from '../Cards/AuthHeaderCard';
import { PhoneInputField } from '../common/Inputs/PhoneNumberField';
import GoogleButton from '../common/GoogleButton/GoogleButton';

const schema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Must be a valid email").required("Email is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^[689]\d{7}$/, "Phone number must be 8 digits starting with 6, 8, or 9"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[A-Z]/, "Password must have at least one uppercase letter")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must have at least one special character")
    .matches(/\d/, "Password must have at least one number"),
});

interface SignUpFormProps {
  onSubmit: (values: any) => void;
  handleSignin: () => void
  loading: boolean;
  error?: string;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSubmit, loading, error, handleSignin }) => {
  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
    },
    validate: yupResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

      const termsOfUse = () => {
        window.open('/Terms_of_Use_for_MYAi.pdf')
      }
    
      const privacyPolicy = () => {
        window.open('/Privacy_Policy_for_MYAi.pdf')
      }

  return (
    <div className="w-full space-y-6">
      <AuthHeader title='Sign Up' subtitle='Enter your details to create an account' />
      {error && <Text className="mt-2 text-red-500">{error}</Text>}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <InputField
          label="First Name"
          name="firstName"
          inputContainerClassName='h-11 sm:h-16'
          placeholder="Enter your first name"
          {...form.getInputProps('firstName')}
          error={form.errors.firstName?.toString()}
        />
        <InputField
          label="Last Name"
          name="lastName"
          inputContainerClassName='h-11 sm:h-16'
          placeholder="Enter your last name"
          {...form.getInputProps('lastName')}
          error={form.errors.lastName?.toString()}
        />
        <InputField
          label="Email Address"
          type="email"
          name="email"
          inputContainerClassName='h-11 sm:h-16'
          placeholder="Enter your email address"
          {...form.getInputProps('email')}
          error={form.errors.email?.toString()}
        />
        <PhoneInputField
          label="Phone Number"
          type="tel"
          name="phone"
          maxLength={8}
          inputContainerClassName='h-11 sm:h-16'
          placeholder="Enter your phone number"
          {...form.getInputProps('phone')}
          error={form.errors.phone?.toString()}
        />
        <InputField
          label="Password"
          type="password"
          name="password"
          inputContainerClassName='h-11 sm:h-16'
          placeholder="Enter your password"
          showPasswordToggle
          {...form.getInputProps('password')}
          error={form.errors.password?.toString()}
        />
        <p className="mb-5 mt-3 text-sm text-gray-600">
        By signing in, you agree to our <a onClick={termsOfUse} className="text-blue-600 cursor-pointer">Terms of Use</a> and <a onClick={privacyPolicy} className="text-blue-600 cursor-pointer">Privacy Policy</a>.
        </p>
        <CustomButton type="submit" loading={loading} disabled={!form.isValid()}>Sign Up</CustomButton>
        <p className="mt-5 text-sm text-center text-gray-600">
          Already have an account? <a onClick={handleSignin} className="text-blue-600 cursor-pointer">Login</a>
        </p>
      </form>
      <div className="flex items-center my-2">
        <hr className="flex-grow border-t border-gray-300" />
        <span className="mx-4 text-gray-500 text-sm font-medium">OR</span>
        <hr className="flex-grow border-t border-gray-300" />
      </div>

      <GoogleButton />
    </div>
  );
};